import { create } from 'zustand';
import type { AlertEvent } from '@/types/event';

// Why this store has TWO views of the same data
// ---------------------------------------------
// ai_worker publishes ONE MQTT message PER detection-positive frame
// (inference_service.py:632 — intentional, keeps live count + bbox fresh).
// All of those messages share the same `event_id` (the alert WINDOW's id,
// matching one row in `ai_events`). Two consumers want different things:
//
//   1. Timeline + counters → want one entry per WINDOW (deduped by event_id)
//   2. Camera tile flash    → wants the firehose (each frame refreshes
//                             bboxes + last-seen-at, fading after ~3s)
//
// We materialize both in the store so neither view has to derive against
// the other in render hot paths.

export interface CameraActivity {
  // The latest event payload — bboxes, person_count, severity all reflect
  // the most recent WS push for this camera. May be older than the open
  // window in `events` if the window closed but operator hasn't acted.
  event: AlertEvent;
  // Wall-clock epoch ms when this most-recent push landed. Drives the
  // "X seconds ago" label and the red-pulse flash window on tiles.
  receivedAt: number;
}

export type PushResult = 'new' | 'update';

interface AlertsState {
  events: AlertEvent[];
  byCamera: Record<string, CameraActivity>;
  maxBuffer: number;
  push: (event: AlertEvent) => PushResult;
  clear: () => void;
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
  events: [],
  byCamera: {},
  maxBuffer: 500,

  push: (event) => {
    const state = get();
    const existingIdx = state.events.findIndex((e) => e.id === event.id);
    const isNew = existingIdx < 0;
    const now = Date.now();

    if (isNew) {
      set({
        events: [event, ...state.events].slice(0, state.maxBuffer),
        byCamera: {
          ...state.byCamera,
          [event.camera_id]: { event, receivedAt: now },
        },
      });
      return 'new';
    }

    // Same event_id arrived again (subsequent frame in the same window).
    // Merge by taking the running max where it makes sense — backend
    // already does this in Mongo via $max so the WS payload may carry the
    // newer value, but defensively coalesce in case of out-of-order delivery.
    const prev = state.events[existingIdx];
    const merged: AlertEvent = {
      ...prev,
      person_count: Math.max(prev.person_count ?? 0, event.person_count ?? 0),
      class_counts: mergeClassCounts(prev.class_counts, event.class_counts),
      // bboxes ALWAYS take the latest — they're the "where is the action
      // right now" signal driving the tile bbox overlay.
      bboxes: event.bboxes ?? prev.bboxes,
      // Latest evidence/snapshot URL wins if newly populated. EvidenceUploaded
      // ack fires AFTER the open frame, so the OPEN row needs to absorb it.
      evidence_url: event.evidence_url ?? prev.evidence_url,
      snapshot_url: event.snapshot_url ?? prev.snapshot_url,
    };
    const updatedEvents = [...state.events];
    updatedEvents[existingIdx] = merged;

    set({
      events: updatedEvents,
      byCamera: {
        ...state.byCamera,
        [event.camera_id]: { event: merged, receivedAt: now },
      },
    });
    return 'update';
  },

  clear: () => set({ events: [], byCamera: {} }),
}));

const mergeClassCounts = (
  a: Record<string, number> | undefined,
  b: Record<string, number> | undefined,
): Record<string, number> | undefined => {
  if (!a) return b;
  if (!b) return a;
  const out: Record<string, number> = { ...a };
  for (const [k, v] of Object.entries(b)) {
    out[k] = Math.max(out[k] ?? 0, v);
  }
  return out;
};
